import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './App.css';
import Home from './components/Home';
import CalculationFormPopup from './components/CalculationFormPopup';
import { ReactComponent as AppstralStar } from './assets/AppstralStar.svg';
import { isMobile, isTablet } from 'react-device-detect';
import Login from './components/Login';
import './fonts.css';
import { UserProvider, useUser } from './contexts/UserContext';
import { AstroGlyph } from './utils/astroIcons';
import { collection, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../src/assets/firebaseConfig';
import { NavLink, useLocation } from 'react-router-dom';
import AppstralWeb from './assets/AppstralWeb-logo.svg';
import DarkAppstralWeb from './assets/Dark-AppstralWeb-logo.svg';
import BellIcon from './assets/icons/BellIcon.svg';
import ProfileIcon from './assets/icons/ProfileIcon.svg';
import ProfileIconWhite from './assets/icons/ProfileIcon-white.svg';
import LoadingSpinner from './components/LoadingSpinner';
import NotifModal from './components/NotifModal';
import lightIcon from './assets/icons/lightIcon.svg';
import darkIcon from './assets/icons/darkIcon.svg';
import ErrorBoundary from './contexts/ErrorBoundary';

// Cada pantalla se baja recien cuando se entra a su ruta: ChartDetails, MyChart y
// ChartPdf solos son ~36k lineas y arrastran react-pdf/jspdf/html2canvas. Cargarlas
// por adelantado hacia que el navegador quedara bloqueado parseando JS en el arranque.
const Calculate = lazy(() => import('./components/Calculate'));
const MyProfile = lazy(() => import('./components/MyProfile'));
const MyChart = lazy(() => import('./components/MyChart'));
const MyCharts = lazy(() => import('./components/MyCharts'));
const ChartPdf = lazy(() => import('./components/ChartPdf'));
const Chat = lazy(() => import('./components/Chat'));
const SearchForm = lazy(() => import('./components/SearchForm'));
const Compatibilidad = lazy(() => import('./components/Compatibilidad'));
const ChartDetails = lazy(() => import('./components/ChartDetails'));
const Calendar = lazy(() => import('./components/Calendar'));

const navItems = [
  { to: '/', label: 'home' },
  { to: '/calcular', label: 'calculate' },
  { to: '/buscador', label: 'buscador' },
  { to: '/calendar', label: 'calendario' },
  { to: '/mi_carta', label: 'my_chart' },
  { to: '/cartas', label: 'charts' },
  { to: '/compatibilidad', label: 'compatibility' },
];
function AppRoutes() {
  const { t, i18n } = useTranslation();
  const { user, loading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [lineStyle, setLineStyle] = useState({});
  const linksRef = useRef({});
  const bellIconRef = useRef(null);
  const [showCalculatePopup, setShowCalculatePopup] = useState(false);
  const [calculateLinkDomElement, setCalculateLinkDomElement] = useState(null);
  const shouldBlockAccess = isMobile || isTablet;
  const handleNavLinkClick = (e, to) => {
    // Mientras la sesion todavia se esta resolviendo no sabemos si hay usuario:
    // dejamos navegar y la ruta se resuelve sola cuando termina de cargar.
    if (!loading && !user && to !== '/') {
      e.preventDefault();
      navigate('/login');
      return;
    }
    if (to === '/calcular') {
      e.preventDefault();
      if (user) {
        setShowCalculatePopup((prevState) => !prevState);
      }
    }
  };
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const sharedMode = localStorage.getItem('dark-mode');
    if (sharedMode !== null) {
      return sharedMode === 'true';
    }
    const currentHour = new Date().getHours();
    return currentHour < 6 || currentHour >= 18;
  });
  const [isIconHovered, setIsIconHovered] = useState(false);
  const [stellaFabExpanded, setStellaFabExpanded] = useState(true);
  useEffect(() => {
    const timeout = setTimeout(() => setStellaFabExpanded(false), 4000);
    return () => clearTimeout(timeout);
  }, []);
  // La elección del toggle se comparte entre pestañas abiertas (localStorage +
  // evento 'storage'), pero no debe sobrevivir a que se cierren todas: se
  // lleva la cuenta de pestañas abiertas y se borra el valor cuando llega a 0.
  useEffect(() => {
    const tabsKey = 'dark-mode-open-tabs';
    const openTabs = Number(localStorage.getItem(tabsKey) || '0') + 1;
    localStorage.setItem(tabsKey, String(openTabs));
    const handleUnload = () => {
      const remaining = Number(localStorage.getItem(tabsKey) || '1') - 1;
      if (remaining <= 0) {
        localStorage.removeItem(tabsKey);
        localStorage.removeItem('dark-mode');
      } else {
        localStorage.setItem(tabsKey, String(remaining));
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    const handleStorage = (event) => {
      if (event.key === 'dark-mode' && event.newValue !== null) {
        setIsDarkMode(event.newValue === 'true');
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    localStorage.setItem('dark-mode', isDarkMode);
  }, [isDarkMode]);
  const toggleDarkMode = () => {
    setIsDarkMode((prevMode) => !prevMode);
  };
  const zodiacSignsEs = [
    'Aries',
    'Tauro',
    'Géminis',
    'Cáncer',
    'Leo',
    'Virgo',
    'Libra',
    'Escorpio',
    'Sagitario',
    'Capricornio',
    'Acuario',
    'Piscis',
  ];
  const zodiacSignsEn = [
    'Aries',
    'Taurus',
    'Gemini',
    'Cancer',
    'Leo',
    'Virgo',
    'Libra',
    'Scorpio',
    'Sagittarius',
    'Capricorn',
    'Aquarius',
    'Pisces',
  ];
  const getSymbolForNotification = (titleEs, titleEn) => {
    const lowerTitleEs = titleEs?.toLowerCase() || '';
    const lowerTitleEn = titleEn?.toLowerCase() || '';
    if (
      lowerTitleEs.includes('luna') ||
      lowerTitleEs.includes('eclipse') ||
      lowerTitleEn.includes('moon') ||
      lowerTitleEn.includes('eclipse')
    ) {
      return { kind: 'planet', name: 'Luna', type: 'moon', sign: null };
    }
    if (lowerTitleEs.includes('retrógrado') || lowerTitleEn.includes('retrograde')) {
      return { kind: 'planet', name: 'Rx', type: 'retrograde', sign: null };
    }
    for (let i = 0; i < zodiacSignsEs.length; i++) {
      const esSign = zodiacSignsEs[i];
      const enSign = zodiacSignsEn[i];
      if (
        lowerTitleEs.includes(esSign.toLowerCase()) ||
        lowerTitleEn.includes(enSign.toLowerCase())
      ) {
        return { kind: 'sign', name: esSign, type: 'zodiac', sign: enSign };
      }
    }
    return { kind: null, name: null, type: 'zodiac', sign: null };
  };
  useEffect(() => {
    const fetchAllNotifications = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      let fetchedNotifications = [];
      let birthdayNotifications = [];
      const qNotifications = query(
        collection(db, 'notificaciones'),
        where('send_time', '>=', Timestamp.fromDate(today)),
        where('send_time', '<', Timestamp.fromDate(tomorrow)),
      );
      try {
        const querySnapshot = await getDocs(qNotifications);
        fetchedNotifications = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          send_time: doc.data().send_time.toDate(),
        }));
      } catch (error) {
        console.error("Error al obtener 'notificaciones': ", error);
      }
      if (user && user.uid) {
        try {
          const qCartas = collection(db, 'users', user.uid, 'cartas');
          const querySnapshotCartas = await getDocs(qCartas);
          querySnapshotCartas.docs.forEach((doc) => {
            const cartaData = doc.data();
            const fechaNacimientoStr = cartaData.fecha;
            const nombre = cartaData.nombre;
            if (fechaNacimientoStr && nombre) {
              const [year, month, day] = fechaNacimientoStr.split('-').map(Number);
              const birthDate = new Date(year, month - 1, day);
              if (
                birthDate.getMonth() === today.getMonth() &&
                birthDate.getDate() === today.getDate()
              ) {
                const titleEs = `Hoy es el cumpleaños de ${nombre}`;
                const titleEn = `Today is ${nombre}'s birthday`;
                const symbolInfo = getSymbolForNotification(titleEs, titleEn);
                birthdayNotifications.push({
                  id: `birthday-${doc.id}`,
                  titleEs: titleEs,
                  titleEn: titleEn,
                  symbol: symbolInfo.symbol,
                  type: 'birthday',
                  sign: symbolInfo.sign,
                  send_time: today,
                });
              }
            }
          });
        } catch (error) {
          console.error(`Error al obtener 'cartas' para el usuario ${user.uid}: `, error);
        }
      }
      const allNotifications = [...fetchedNotifications, ...birthdayNotifications];
      setNotifications(allNotifications);
      setHasNewNotifications(allNotifications.length > 0);
    };
    if (user) {
      fetchAllNotifications();
    }
  }, [user]);
  useEffect(() => {
    const calculateNavLinkElement = document.getElementById('calculateNavLink');
    if (calculateNavLinkElement) {
      setCalculateLinkDomElement(calculateNavLinkElement);
    }
  }, []);
  useEffect(() => {
    const updateLine = () => {
      if (location.pathname === '/perfil' || showCalculatePopup) {
        setLineStyle({ display: 'none' });
        return;
      }
      const activeLink = linksRef.current[location.pathname];
      if (activeLink) {
        const { offsetLeft, offsetWidth } = activeLink;
        setLineStyle({
          transform: `translateX(${offsetLeft}px)`,
          width: `${offsetWidth}px`,
          display: 'block',
        });
      } else {
        setLineStyle({ display: 'none' });
      }
    };
    const timeout = setTimeout(updateLine, 10);
    const observer = new ResizeObserver(updateLine);
    Object.values(linksRef.current).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => {
      clearTimeout(timeout);
      Object.values(linksRef.current).forEach((el) => {
        if (el) observer.unobserve(el);
      });
    };
  }, [location.pathname, t, showCalculatePopup]);
  const handleCalculateFormSubmit = (formData) => {
    navigate('/calcular', { state: { resultado: formData } });
    setShowCalculatePopup(false);
  };
  const isBellDisabled = notifications.length === 0;
  if (!loading && !user && location.pathname !== '/') {
    return <Login />;
  }
  // Mientras `loading` es true no sabemos si hay sesion: no redirigimos a /login,
  // solo dejamos la zona de contenido vacia (el spinner ya se muestra encima).
  const requireUser = (element) => {
    if (loading) return null;
    return user ? element : <Navigate to="/login" />;
  };
  return (
    <div className="App">
      <div className="header-main">
        <img
          onClick={() => navigate('/')}
          src={isDarkMode ? DarkAppstralWeb : AppstralWeb}
          className="appstral-logo"
          alt="Appstral Web Logo"
        />
        <nav className="nav">
          <div
            onMouseEnter={() => setIsIconHovered(true)}
            onMouseLeave={() => setIsIconHovered(false)}
            onClick={toggleDarkMode}
            className={`theme-switcher-container ${isDarkMode ? 'theme-dark-mode' : 'theme-light-mode'}`}
          >
            <img src={lightIcon} alt="Light Mode" className={`theme-icon theme-icon-light`} />
            <div className={`theme-switcher-divider`}></div>
            <img src={darkIcon} alt="Dark Mode" className={`theme-icon theme-icon-dark`} />
          </div>
          <div
            ref={bellIconRef}
            className={`notification-bell-container ${showNotificationsModal ? 'modal-active' : ''} ${isBellDisabled ? 'disabled' : ''}`}
            onClick={
              !isBellDisabled
                ? () => {
                    setShowNotificationsModal((prevState) => !prevState);
                    setHasNewNotifications(false);
                  }
                : undefined
            }
            style={{
              cursor: isBellDisabled ? 'default' : 'pointer',
              position: 'relative',
              display: 'flex',
              width: 'fit-content',
              justifyContent: 'center',
              alignContent: 'center',
              alignItems: 'center',
            }}
          >
            <img src={BellIcon} alt="Notifications" className="bell-icon" />
            {hasNewNotifications && !isBellDisabled && (
              <span
                style={{
                  position: 'absolute',
                  top: '.5rem',
                  right: '-.1rem',
                  width: '.55rem',
                  height: '.55rem',
                  backgroundColor: 'red',
                  borderRadius: '50%',
                  border: '1px solid var(--grey-bg)',
                }}
              />
            )}
          </div>
          <NavLink
            to="/perfil"
            className={({ isActive }) =>
              isActive ? 'header-profile active-profile' : 'header-profile'
            }
          >
            {({ isActive }) => (
              <div className="header-profile">
                <img src={isActive ? ProfileIconWhite : ProfileIcon} alt="Profile Icon" />
                <p> {t('myprofile')}</p>
              </div>
            )}
          </NavLink>
        </nav>
      </div>
      <nav className="nav-container">
        <ul className="nav-list">
          {navItems.map(({ to, label }) => (
            <li key={to}>
              <NavLink
                to={to}
                ref={(el) => {
                  linksRef.current[to] = el;
                  if (to === '/calcular') {
                    setCalculateLinkDomElement(el);
                  }
                }}
                className={({ isActive }) => (isActive ? `nav-link active` : `nav-link`)}
                onClick={(e) => handleNavLinkClick(e, to)}
              >
                {t(label)}
              </NavLink>
            </li>
          ))}
        </ul>
        {location.pathname !== '/stella' && (
          <div
            className={`stella-fab-wrapper ${stellaFabExpanded ? 'expanded' : 'collapsed'}`}
            style={{
              flexDirection: 'row',
              display: 'flex',
              gap: '4px',
              alignItems: 'center',
              paddingTop: '6px',
            }}
          >
            <NavLink to="/stella" className={`chat-link`}>
              <span className="chat-link-desktop-only">{t('consult_stella')}</span>{' '}
              <span className="chat-link-brand-word">Stella</span>
            </NavLink>
            <AppstralStar alt={t('loginPage.appstralStarAlt')} className="appstral-star-icon" />
          </div>
        )}
      </nav>
      {}
      {user && (
        <CalculationFormPopup
          isOpen={showCalculatePopup}
          onClose={() => setShowCalculatePopup(false)}
          onSubmit={handleCalculateFormSubmit}
          anchorEl={calculateLinkDomElement}
        />
      )}
      <NotifModal
        isOpen={showNotificationsModal}
        onClose={() => setShowNotificationsModal(false)}
        anchorEl={bellIconRef.current}
      >
        <p
          style={{
            margin: '0',
            textAlign: 'end',
            fontWeight: 300,
            right: '1rem',
            top: '.6rem',
            position: 'absolute',
            fontSize: '.85rem',
          }}
        >
          Hoy
        </p>
        {notifications.length > 0 ? (
          notifications.map((notification) => {
            let currentTitle;
            let currentBody = '';
            let glyphKind;
            let glyphName;
            let notificationType;
            if (notification.type === 'birthday') {
              currentTitle = i18n.language === 'es' ? notification.titleEs : notification.titleEn;
              glyphKind = 'planet';
              glyphName = 'Sol';
              notificationType = 'birthday';
            } else {
              currentTitle = i18n.language === 'es' ? notification.title_es : notification.title_en;
              currentBody = i18n.language === 'es' ? notification.body_es : notification.body_en;
              const symbolInfo = getSymbolForNotification(
                notification.title_es,
                notification.title_en,
              );
              glyphKind = symbolInfo.kind;
              glyphName = symbolInfo.name;
              notificationType = symbolInfo.type;
            }
            const gradientClass =
              notificationType === 'birthday'
                ? 'gradient-birthday'
                : `gradient-${notificationType}`;
            return (
              <div key={notification.id} className="notification-item">
                <div className={`symbol-circle ${gradientClass}`}>
                  <AstroGlyph
                    kind={glyphKind}
                    name={glyphName}
                    size="1.55rem"
                    style={{ color: 'white' }}
                  />
                </div>
                <div className="notification-text-content">
                  <h3>{currentTitle}</h3>
                  {currentBody && <p>{currentBody}</p>}
                </div>
              </div>
            );
          })
        ) : (
          <p style={{ color: '#777' }}>{t('noNotificationsToday')}</p>
        )}
      </NotifModal>
      {loading && <LoadingSpinner />}
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={loading ? null : <Home />} />
          {}
          <Route path="/calcular" element={requireUser(<Calculate />)} />
          <Route path="/perfil" element={requireUser(<MyProfile />)} />
          <Route path="/cartas" element={requireUser(<MyCharts />)} />
          <Route path="/chart-pdf" element={requireUser(<ChartPdf />)} />
          <Route path="/calendar" element={requireUser(<Calendar />)} />
          <Route path="/mi_carta" element={requireUser(<MyChart />)} />
          <Route path="/stella" element={requireUser(<Chat />)} />
          <Route path="/carta/:cartaId" element={requireUser(<ChartDetails />)} />
          <Route path="/buscador" element={requireUser(<SearchForm />)} />
          <Route path="/compatibilidad" element={requireUser(<Compatibilidad />)} />
          {}
          <Route path="/login" element={<Login />} />
          {}
          <Route path="*" element={loading ? null : <Navigate to={user ? '/' : '/login'} />} />
        </Routes>
      </Suspense>
    </div>
  );
}
function App() {
  return (
    <UserProvider>
      <ErrorBoundary>
        <Router>
          <AppRoutes />
        </Router>
      </ErrorBoundary>
    </UserProvider>
  );
}
export default App;
